import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Globe, User, Building, Phone, Mail, MapPin, Briefcase } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getAccessToken } from '../../services/tokenStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

async function fetchUsers() {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/admin/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch users');
  return res.json();
}

async function generateWebsite(data: any) {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/admin/generate-website`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to generate website');
  return res.json();
}

const trades = [
  'Plumbing', 'HVAC', 'Electrical', 'Roofing', 'Landscaping',
  'Painting', 'Cleaning', 'Handyman', 'Moving', 'Pest Control',
  'Pool Service', 'Garage Door', 'Locksmith', 'Appliance Repair',
];

export default function GenerateWebsitePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // User selection
    userId: '',
    newUserEmail: '',
    createNewUser: false,
    // Business info
    businessName: '',
    trade: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    description: '',
    // Domain
    domain: '',
  });
  const [result, setResult] = useState<any>(null);

  const { data: usersData } = useQuery({
    queryKey: ['admin-users'],
    queryFn: fetchUsers,
  });

  const users = usersData?.data?.users || [];

  const mutation = useMutation({
    mutationFn: generateWebsite,
    onSuccess: (data) => {
      setResult(data.data);
      setStep(4);
    },
  });

  const handleSubmit = () => {
    const userId = formData.createNewUser ? formData.newUserEmail : formData.userId;

    mutation.mutate({
      userId,
      domain: formData.domain || formData.businessName.toLowerCase().replace(/\s+/g, '-'),
      businessInfo: {
        businessName: formData.businessName,
        trade: formData.trade,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        description: formData.description,
      },
    });
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <Link
        to="/admin"
        className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft size={20} />
        Back to Dashboard
      </Link>

      <h1 className="text-3xl font-bold text-white mb-2">Generate Website</h1>
      <p className="text-gray-400 mb-8">Create a new website for a client</p>

      {/* Progress Steps */}
      <div className="flex items-center gap-4 mb-8">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-700 text-gray-400'
              }`}
            >
              {s}
            </div>
            {s < 4 && (
              <div className={`w-12 h-1 ${step > s ? 'bg-emerald-500' : 'bg-gray-700'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Select User */}
      {step === 1 && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <User size={20} />
            Select or Create User
          </h2>

          <div className="space-y-4">
            <label className="flex items-center gap-3 p-4 bg-gray-700 rounded-lg cursor-pointer">
              <input
                type="radio"
                checked={!formData.createNewUser}
                onChange={() => setFormData({ ...formData, createNewUser: false })}
                className="text-emerald-500"
              />
              <span className="text-white">Select existing user</span>
            </label>

            {!formData.createNewUser && (
              <select
                value={formData.userId}
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="">Select a user...</option>
                {users.map((user: any) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            )}

            <label className="flex items-center gap-3 p-4 bg-gray-700 rounded-lg cursor-pointer">
              <input
                type="radio"
                checked={formData.createNewUser}
                onChange={() => setFormData({ ...formData, createNewUser: true })}
                className="text-emerald-500"
              />
              <span className="text-white">Create new user</span>
            </label>

            {formData.createNewUser && (
              <input
                type="email"
                placeholder="New user email"
                value={formData.newUserEmail}
                onChange={(e) => setFormData({ ...formData, newUserEmail: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            )}
          </div>

          <button
            onClick={() => setStep(2)}
            disabled={!formData.userId && !formData.newUserEmail}
            className="mt-6 w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            Continue
          </button>
        </div>
      )}

      {/* Step 2: Business Info */}
      {step === 2 && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Building size={20} />
            Business Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-gray-400 mb-2">Business Name *</label>
              <input
                type="text"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="Acme Plumbing"
              />
            </div>

            <div>
              <label className="block text-gray-400 mb-2">Trade *</label>
              <select
                value={formData.trade}
                onChange={(e) => setFormData({ ...formData, trade: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="">Select trade...</option>
                {trades.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-400 mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-gray-400 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="contact@acmeplumbing.com"
              />
            </div>

            <div>
              <label className="block text-gray-400 mb-2">City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="Phoenix"
              />
            </div>

            <div>
              <label className="block text-gray-400 mb-2">State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="AZ"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-gray-400 mb-2">Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
                placeholder="123 Main St"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-gray-400 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white h-24"
                placeholder="Brief description of the business..."
              />
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={() => setStep(1)}
              className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={!formData.businessName || !formData.trade}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Domain & Confirm */}
      {step === 3 && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Globe size={20} />
            Domain & Confirmation
          </h2>

          <div className="mb-6">
            <label className="block text-gray-400 mb-2">Subdomain</label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.domain || formData.businessName.toLowerCase().replace(/\s+/g, '-')}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                className="flex-1 p-3 bg-gray-700 border border-gray-600 rounded-l-lg text-white"
              />
              <span className="p-3 bg-gray-600 border border-gray-600 border-l-0 rounded-r-lg text-gray-300">
                .foundlio.site
              </span>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-700 rounded-lg p-4 mb-6">
            <h3 className="text-white font-medium mb-3">Summary</h3>
            <div className="space-y-2 text-sm">
              <p className="text-gray-400">
                <span className="text-gray-300">Business:</span> {formData.businessName}
              </p>
              <p className="text-gray-400">
                <span className="text-gray-300">Trade:</span> {formData.trade}
              </p>
              <p className="text-gray-400">
                <span className="text-gray-300">Location:</span> {formData.city}, {formData.state}
              </p>
              <p className="text-gray-400">
                <span className="text-gray-300">URL:</span>{' '}
                <span className="text-emerald-400">
                  https://{formData.domain || formData.businessName.toLowerCase().replace(/\s+/g, '-')}.foundlio.site
                </span>
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => setStep(2)}
              className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={mutation.isPending}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {mutation.isPending ? 'Generating...' : 'Generate Website'}
            </button>
          </div>

          {mutation.isError && (
            <p className="text-red-400 mt-4 text-center">
              Error: {(mutation.error as Error).message}
            </p>
          )}
        </div>
      )}

      {/* Step 4: Success */}
      {step === 4 && result && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 text-center">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe className="text-emerald-400" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Website Generated!</h2>
          <p className="text-gray-400 mb-6">{result.message}</p>

          <div className="bg-gray-700 rounded-lg p-4 mb-6">
            <p className="text-gray-400 mb-2">Preview URL:</p>
            <a
              href={result.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:underline text-lg"
            >
              {result.previewUrl}
            </a>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => {
                setStep(1);
                setFormData({
                  userId: '',
                  newUserEmail: '',
                  createNewUser: false,
                  businessName: '',
                  trade: '',
                  phone: '',
                  email: '',
                  address: '',
                  city: '',
                  state: '',
                  description: '',
                  domain: '',
                });
                setResult(null);
              }}
              className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              Generate Another
            </button>
            <Link
              to="/admin/websites"
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors text-center"
            >
              View All Websites
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
